"use client";

import { useEffect, useRef, useState } from "react";
import { OptimizedImage } from "./OptimizedImage";
import type { OptimizedImageProps } from "./OptimizedImage";

export const ViewportAnimatedImage = (props: OptimizedImageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const [targetOffset, setTargetOffset] = useState(0);

  const lastScrollY = useRef(0);
  const animationFrame = useRef<number>();
  const elementTop = useRef(0);

  // Smooth parallax factor
  const PARALLAX_FACTOR = 0.08;
  const LERP_FACTOR = 0.1; // Linear interpolation for smooth movement

  useEffect(() => {
    let animationFrameId: number;

    // Smooth lerp animation loop
    const smoothUpdate = () => {
      setParallaxOffset((current) => {
        const diff = targetOffset - current;
        if (Math.abs(diff) < 0.01) return targetOffset;
        return current + diff * LERP_FACTOR;
      });

      animationFrameId = requestAnimationFrame(smoothUpdate);
    };

    if (isInView) {
      smoothUpdate();
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isInView, targetOffset]);

  useEffect(() => {
    lastScrollY.current = window.scrollY;

    // IntersectionObserver to detect when element is in viewport
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.1;

        setIsInView(isVisible);

        // Reset when entering viewport
        if (isVisible && !isInView) {
          const element = ref.current;
          if (element) {
            elementTop.current =
              element.getBoundingClientRect().top + window.scrollY;
          }
          lastScrollY.current = window.scrollY;
          setParallaxOffset(0);
          setTargetOffset(0);
        }
      },
      {
        threshold: [0, 0.1, 0.5, 1.0],
        rootMargin: "100px 0px",
      },
    );

    // Smooth parallax scroll handler
    const handleScroll = () => {
      if (!isInView) return;

      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }

      animationFrame.current = requestAnimationFrame(() => {
        const element = ref.current;

        if (element) {
          // Calculate smooth parallax based on viewport position
          const elementRect = element.getBoundingClientRect();
          const viewportCenter = window.innerHeight / 2;
          const elementCenter = elementRect.top + elementRect.height / 2;
          const distanceFromCenter = elementCenter - viewportCenter;

          // Set target offset for smooth interpolation
          const newTargetOffset = -distanceFromCenter * PARALLAX_FACTOR;
          setTargetOffset(newTargetOffset);
        }

        lastScrollY.current = window.scrollY;
      });
    };

    // Setup
    const element = ref.current;
    if (element) {
      observer.observe(element);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);

      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [isInView]);

  return (
    <div
      ref={ref}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView
          ? `translate3d(0, ${parallaxOffset}px, 0) scale(${
              1 + Math.abs(parallaxOffset) * 0.0001
            })`
          : "translate3d(0, 60px, 0) scale(0.92)",
        filter: isInView ? "blur(0px)" : "blur(2px)",
        transition: isInView
          ? "opacity 1200ms cubic-bezier(0.19, 1, 0.22, 1), filter 1200ms cubic-bezier(0.19, 1, 0.22, 1)"
          : "all 1000ms cubic-bezier(0.19, 1, 0.22, 1)",
        willChange: isInView ? "transform" : "auto",
        backfaceVisibility: "hidden",
        perspective: 1000,
      }}
    >
      <OptimizedImage {...props} />
    </div>
  );
};
